import { loadSafetyPageData, saveWeights, saveRuleSelection, RULE_LABELS, RULE_KEY_ORDER } from './safety-data';
import { runSafetyAnalysis, updateSafetyAnalysisUnit } from './safety-analysis';
import { isSystemGroupId, expandGroups } from '../api/group-hierarchy';
import type { SafetyPageData, RuleConfig, ScoreBy, DistUnit } from './safety-types';
import {
  MEASUREMENT_UNIT_EVENT, getMeasurementUnit, setMeasurementUnit,
  type MeasurementUnit,
} from '../utils/units';
import { updateSafetyResultUnit } from './safety-results';

// ── Module state ───────────────────────────────────────────────────────────

let _wired      = false;
let _dataLoaded = false;
let _data:        SafetyPageData | null = null;
let _scoreBy:     ScoreBy   = 'driver';
let _distUnit:    DistUnit   = getMeasurementUnit();
let _ruleConfigs: RuleConfig[] = [];  // live-edited copy

// ── Public API ─────────────────────────────────────────────────────────────

export function initSafetyPage(): void {
  if (_wired) return;
  _wired = true;

  initDateRange();
  wireScoreBy();
  wireQuickRanges();
  wireDistUnit();
  wireVselector();
  wireDselector();
  wireGroupSelector();
  wireRunButton();
  wireExportButton();
  wireScoringInfoModal();

  loadData();
}

export function resetSafetyPage(): void {
  _wired      = false;
  _dataLoaded = false;
  _data       = null;
  _ruleConfigs = [];

  const ruleConfig = document.getElementById('safetyRuleConfig');
  if (ruleConfig) ruleConfig.innerHTML = '<div class="sc2-rule-loading">Loading rules…</div>';

  const vselectorList = document.getElementById('safetyVselectorList');
  if (vselectorList) vselectorList.innerHTML = '';

  const dselectorList = document.getElementById('safetyDselectorList');
  if (dselectorList) dselectorList.innerHTML = '';

  const gselectorList = document.getElementById('safetyGselectorList');
  if (gselectorList) gselectorList.innerHTML = '';

  const vsLabel = document.getElementById('safetyVselectorLabel') as HTMLElement;
  if (vsLabel) vsLabel.textContent = 'Loading…';

  const dsLabel = document.getElementById('safetyDselectorLabel') as HTMLElement;
  if (dsLabel) dsLabel.textContent = 'All Drivers';

  const gsLabel = document.getElementById('safetyGselectorLabel') as HTMLElement;
  if (gsLabel) gsLabel.textContent = 'Loading…';

  const runBtn = document.getElementById('safetyRunBtn') as HTMLButtonElement;
  if (runBtn) runBtn.disabled = true;

  updateWeightStatus();
  showState('empty');
}

// ── Date range ─────────────────────────────────────────────────────────────

function initDateRange(): void {
  applyQuickRange('30d');
}

function applyQuickRange(range: string): void {
  const now  = new Date();
  const to   = document.getElementById('safetyTo')   as HTMLInputElement;
  const from = document.getElementById('safetyFrom') as HTMLInputElement;
  to.value = now.toISOString().slice(0, 10);
  if (range === '7d')  from.value = new Date(now.getTime() - 7  * 86400_000).toISOString().slice(0, 10);
  if (range === '30d') from.value = new Date(now.getTime() - 30 * 86400_000).toISOString().slice(0, 10);
  if (range === 'mtd') from.value = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  if (range === 'ytd') from.value = new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10);
}

function wireQuickRanges(): void {
  document.querySelectorAll('[data-safety-range]').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('[data-safety-range]').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      applyQuickRange((btn as HTMLElement).dataset.safetyRange!);
    });
  });
}

// ── Score By toggle ────────────────────────────────────────────────────────

function wireScoreBy(): void {
  document.getElementById('safetyByDriver')! .addEventListener('click', () => setScoreBy('driver'));
  document.getElementById('safetyByVehicle')!.addEventListener('click', () => setScoreBy('vehicle'));
  setScoreBy(_scoreBy);  // apply initial visibility state
}

function setScoreBy(mode: ScoreBy): void {
  _scoreBy = mode;
  document.getElementById('safetyByDriver')! .classList.toggle('active', mode === 'driver');
  document.getElementById('safetyByVehicle')!.classList.toggle('active', mode === 'vehicle');
  const isDriver  = mode === 'driver';
  const isVehicle = mode === 'vehicle';
  // Driver filter only shown in driver mode
  document.getElementById('safetyDriverGroupWrap')!.classList.toggle('hidden', !isDriver);
  document.getElementById('safetyDriverDivider')!  .classList.toggle('hidden', !isDriver);
  // Vehicle selector only shown in vehicle mode
  document.getElementById('safetyVehicleGroup')!   .classList.toggle('hidden', !isVehicle);
  document.getElementById('safetyVehicleDivider')! .classList.toggle('hidden', !isVehicle);
}

// ── Distance unit toggle ───────────────────────────────────────────────────

function wireDistUnit(): void {
  document.getElementById('safetyUnitKm')!.addEventListener('click', () => setMeasurementUnit('km'));
  document.getElementById('safetyUnitMi')!.addEventListener('click', () => setMeasurementUnit('mi'));
  setDistUnit(getMeasurementUnit());
  window.addEventListener(MEASUREMENT_UNIT_EVENT, (e) =>
    setDistUnit((e as CustomEvent<MeasurementUnit>).detail));
}

function setDistUnit(unit: DistUnit): void {
  _distUnit = unit;
  document.getElementById('safetyUnitKm')!.classList.toggle('active', unit === 'km');
  document.getElementById('safetyUnitMi')!.classList.toggle('active', unit === 'mi');
  updateSafetyResultUnit(unit);
  updateSafetyAnalysisUnit(unit);
}

// ── Scoring info modal ─────────────────────────────────────────────────────

function wireScoringInfoModal(): void {
  document.getElementById('safetyInfoBtn')!.addEventListener('click', () =>
    document.getElementById('safetyInfoModal')!.classList.add('show')
  );
  document.getElementById('safetyInfoModalClose')!.addEventListener('click', () =>
    document.getElementById('safetyInfoModal')!.classList.remove('show')
  );
  document.getElementById('safetyInfoModal')!.addEventListener('click', (e) => {
    if ((e.target as Element).id === 'safetyInfoModal')
      document.getElementById('safetyInfoModal')!.classList.remove('show');
  });
}

// ── Vehicle selector ───────────────────────────────────────────────────────

function wireVselector(): void {
  const toggle = document.getElementById('safetyVselectorToggle')!;
  const panel  = document.getElementById('safetyVselectorPanel')!;
  panel.addEventListener('click', (e) => e.stopPropagation());
  toggle.addEventListener('click', (e) => {
    e.stopPropagation();
    const opening = panel.classList.contains('hidden');
    panel.classList.toggle('hidden');
    document.getElementById('safetyDselectorPanel')!.classList.add('hidden');
    document.getElementById('safetyGselectorPanel')!.classList.add('hidden');
    if (!opening) resetVsSearch();
  });
  document.getElementById('safetyVselectAll')!.addEventListener('click', () => {
    document.querySelectorAll<HTMLInputElement>('#safetyVselectorList input[type="checkbox"]')
      .forEach(cb => { cb.checked = true; });
    updateVselectorLabel();
  });
  document.getElementById('safetyVselectNone')!.addEventListener('click', () => {
    document.querySelectorAll<HTMLInputElement>('#safetyVselectorList input[type="checkbox"]')
      .forEach(cb => { cb.checked = false; });
    updateVselectorLabel();
  });

  document.getElementById('safetyVsSearch')?.addEventListener('input', (e) => {
    const q = (e.target as HTMLInputElement).value.toLowerCase().trim();
    document.querySelectorAll<HTMLElement>('#safetyVselectorList .vselector-item').forEach(row => {
      const name = row.textContent?.toLowerCase().trim() ?? '';
      row.style.display = !q || name.includes(q) ? '' : 'none';
    });
    document.querySelectorAll<HTMLElement>('#safetyVselectorList .vselector-group-hdr').forEach(hdr => {
      if (!q) { hdr.style.removeProperty('display'); return; }
      let next = hdr.nextElementSibling as HTMLElement | null;
      let hasVisible = false;
      while (next && !next.classList.contains('vselector-group-hdr')) {
        if (next.style.display !== 'none') { hasVisible = true; break; }
        next = next.nextElementSibling as HTMLElement | null;
      }
      hdr.style.display = hasVisible ? '' : 'none';
    });
  });
}

function resetVsSearch(): void {
  const inp = document.getElementById('safetyVsSearch') as HTMLInputElement | null;
  if (inp) inp.value = '';
  document.querySelectorAll<HTMLElement>('#safetyVselectorList .vselector-item, #safetyVselectorList .vselector-group-hdr')
    .forEach(el => el.style.removeProperty('display'));
}

function updateVselectorLabel(): void {
  const all     = document.querySelectorAll('#safetyVselectorList input[type="checkbox"]').length;
  const checked = document.querySelectorAll('#safetyVselectorList input[type="checkbox"]:checked').length;
  const el = document.getElementById('safetyVselectorLabel')!;
  el.textContent = checked === all ? `All Vehicles (${all})` : `${checked} of ${all} selected`;
}

function populateVselector(data: SafetyPageData): void {
  const list = document.getElementById('safetyVselectorList')!;
  list.innerHTML = '';

  const byGroup = new Map<string, typeof data.devices>();
  data.devices.forEach(dev => {
    const gid = dev.groupIds.find(id => !isSystemGroupId(id)) ?? '__none__';
    if (!byGroup.has(gid)) byGroup.set(gid, []);
    byGroup.get(gid)!.push(dev);
  });

  const sorted = [...byGroup.entries()].sort(([a], [b]) => {
    if (a === '__none__') return 1;
    if (b === '__none__') return -1;
    return (data.groupMap.get(a) ?? a).localeCompare(data.groupMap.get(b) ?? b);
  });

  sorted.forEach(([gid, devices]) => {
    const groupName = gid === '__none__' ? 'Ungrouped' : (data.groupMap.get(gid) ?? gid);
    const hdr = document.createElement('div');
    hdr.className = 'vselector-group-hdr';
    hdr.textContent = groupName;
    list.appendChild(hdr);

    devices.forEach(dev => {
      const lbl = document.createElement('label');
      lbl.className = 'vselector-item';
      lbl.innerHTML = `<input type="checkbox" value="${escAttr(dev.id)}" checked> ${escHtml(dev.name)}`;
      lbl.querySelector('input')!.addEventListener('change', updateVselectorLabel);
      list.appendChild(lbl);
    });
  });

  updateVselectorLabel();
}

// ── Driver selector ────────────────────────────────────────────────────────

function wireDselector(): void {
  const toggle = document.getElementById('safetyDselectorToggle')!;
  const panel  = document.getElementById('safetyDselectorPanel')!;
  panel.addEventListener('click', (e) => e.stopPropagation());
  toggle.addEventListener('click', (e) => {
    e.stopPropagation();
    const opening = panel.classList.contains('hidden');
    panel.classList.toggle('hidden');
    document.getElementById('safetyVselectorPanel')!.classList.add('hidden');
    document.getElementById('safetyGselectorPanel')!.classList.add('hidden');
    if (!opening) resetDsSearch();
  });
  document.getElementById('safetyDselectAll')!.addEventListener('click', () => {
    document.querySelectorAll<HTMLInputElement>('#safetyDselectorList input[type="checkbox"]')
      .forEach(cb => { cb.checked = true; });
    updateDselectorLabel();
  });
  document.getElementById('safetyDselectNone')!.addEventListener('click', () => {
    document.querySelectorAll<HTMLInputElement>('#safetyDselectorList input[type="checkbox"]')
      .forEach(cb => { cb.checked = false; });
    updateDselectorLabel();
  });

  document.getElementById('safetyDsSearch')?.addEventListener('input', (e) => {
    const q = (e.target as HTMLInputElement).value.toLowerCase().trim();
    document.querySelectorAll<HTMLElement>('#safetyDselectorList .vselector-item').forEach(row => {
      const name = row.textContent?.toLowerCase().trim() ?? '';
      row.style.display = !q || name.includes(q) ? '' : 'none';
    });
  });
}

function resetDsSearch(): void {
  const inp = document.getElementById('safetyDsSearch') as HTMLInputElement | null;
  if (inp) inp.value = '';
  document.querySelectorAll<HTMLElement>('#safetyDselectorList .vselector-item')
    .forEach(el => el.style.removeProperty('display'));
}

function updateDselectorLabel(): void {
  const all     = document.querySelectorAll('#safetyDselectorList input[type="checkbox"]').length;
  const checked = document.querySelectorAll('#safetyDselectorList input[type="checkbox"]:checked').length;
  const el = document.getElementById('safetyDselectorLabel')!;
  el.textContent = checked === all ? `All Drivers (${all})` : `${checked} of ${all} selected`;
}

// ── Group selector ─────────────────────────────────────────────────────────

function wireGroupSelector(): void {
  const toggle = document.getElementById('safetyGselectorToggle')!;
  const panel  = document.getElementById('safetyGselectorPanel')!;
  panel.addEventListener('click', (e) => e.stopPropagation());
  toggle.addEventListener('click', (e) => {
    e.stopPropagation();
    const opening = panel.classList.contains('hidden');
    panel.classList.toggle('hidden');
    document.getElementById('safetyVselectorPanel')!.classList.add('hidden');
    document.getElementById('safetyDselectorPanel')!.classList.add('hidden');
    if (!opening) resetGsSearch();
  });
  document.getElementById('safetyGselectAll')!.addEventListener('click', () => {
    document.querySelectorAll<HTMLInputElement>('#safetyGselectorList input[type="checkbox"]')
      .forEach(cb => { cb.checked = true; });
    updateGroupSelectorLabel();
  });
  document.getElementById('safetyGselectNone')!.addEventListener('click', () => {
    document.querySelectorAll<HTMLInputElement>('#safetyGselectorList input[type="checkbox"]')
      .forEach(cb => { cb.checked = false; });
    updateGroupSelectorLabel();
  });

  document.getElementById('safetyGsSearch')?.addEventListener('input', (e) => {
    const q = (e.target as HTMLInputElement).value.toLowerCase().trim();
    document.querySelectorAll<HTMLElement>('#safetyGselectorList .vselector-item').forEach(row => {
      const name = row.textContent?.toLowerCase().trim() ?? '';
      row.style.display = !q || name.includes(q) ? '' : 'none';
    });
  });
}

function resetGsSearch(): void {
  const inp = document.getElementById('safetyGsSearch') as HTMLInputElement | null;
  if (inp) inp.value = '';
  document.querySelectorAll<HTMLElement>('#safetyGselectorList .vselector-item')
    .forEach(el => el.style.removeProperty('display'));
}

function updateGroupSelectorLabel(): void {
  const all     = document.querySelectorAll('#safetyGselectorList input[type="checkbox"]').length;
  const checked = document.querySelectorAll('#safetyGselectorList input[type="checkbox"]:checked').length;
  const el = document.getElementById('safetyGselectorLabel');
  if (!el) return;
  el.textContent = checked === all ? `All Groups (${all})` : `${checked} / ${all} groups`;
}

function populateGroupSelector(data: SafetyPageData): void {
  const list = document.getElementById('safetyGselectorList')!;
  list.innerHTML = '';

  // List every group (parents + children + empty), not just those directly on a vehicle.
  if (data.groups.length === 0) {
    list.innerHTML = '<div style="padding:0.4rem 0.75rem;font-size:0.65rem;color:var(--muted)">No groups found</div>';
    const gsLabel = document.getElementById('safetyGselectorLabel');
    if (gsLabel) gsLabel.textContent = 'No Groups';
    return;
  }

  data.groups.forEach(({ id, name, depth }) => {
    const lbl = document.createElement('label');
    lbl.className = 'vselector-item';
    // Indent children so the parent → child hierarchy is visible (base left padding is 0.75rem).
    if (depth > 0) lbl.style.paddingLeft = `${0.75 + depth * 0.85}rem`;
    lbl.innerHTML = `<input type="checkbox" value="${escAttr(id)}" checked><span class="vselector-item-name">${escHtml(name)}</span>`;
    lbl.querySelector('input')!.addEventListener('change', updateGroupSelectorLabel);
    list.appendChild(lbl);
  });

  updateGroupSelectorLabel();
}

function getSelectedGroupIds(): Set<string> {
  return new Set(
    [...document.querySelectorAll<HTMLInputElement>('#safetyGselectorList input[type="checkbox"]:checked')]
      .map(cb => cb.value)
  );
}

function populateDselector(data: SafetyPageData): void {
  const list = document.getElementById('safetyDselectorList')!;
  list.innerHTML = '';

  const sorted = [...data.userMap.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  sorted.forEach(([uid, name]) => {
    const lbl = document.createElement('label');
    lbl.className = 'vselector-item';
    lbl.innerHTML = `<input type="checkbox" value="${escAttr(uid)}" checked> ${escHtml(name)}`;
    lbl.querySelector('input')!.addEventListener('change', updateDselectorLabel);
    list.appendChild(lbl);
  });

  updateDselectorLabel();
}

// ── Rule visual metadata ───────────────────────────────────────────────────

const RULE_STYLE: Record<string, { color: string; icon: string }> = {
  hardAccel: {
    color: '#f97316',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>',
  },
  harshBraking: {
    color: '#ef4444',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="currentColor"/></svg>',
  },
  harshCornering: {
    color: '#a855f7',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 10 20 15 15 20"/><path d="M4 4v7a4 4 0 0 0 4 4h12"/></svg>',
  },
  seatBelt: {
    color: '#06b6d4',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
  },
  speeding: {
    color: '#eab308',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a10 10 0 0 1 7.38 16.75"/><path d="M12 2a10 10 0 0 0-7.38 16.75"/><path d="m12 12-2.5-4.5"/><circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none"/></svg>',
  },
  idling: {
    color: '#64748b',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
  },
};

// ── Rule Config Panel ──────────────────────────────────────────────────────

function renderRuleConfig(): void {
  const panel = document.getElementById('safetyRuleConfig')!;
  panel.innerHTML = '';

  if (_ruleConfigs.length === 0) {
    panel.innerHTML = '<div class="sc2-rule-loading">No matching rules found in this database.</div>';
    return;
  }

  _ruleConfigs.forEach(rule => {
    const displayName = RULE_LABELS[rule.key];
    const style = RULE_STYLE[rule.key] ?? { color: 'var(--accent)', icon: '' };

    const card = document.createElement('div');
    card.className = 'sc2-rule-card ' + (rule.enabled ? 'active' : 'inactive');
    card.dataset.key = rule.key;
    card.style.setProperty('--rule-color', style.color);

    card.innerHTML = `
      <div class="sc2-rc-header">
        <div class="sc2-rc-icon">${style.icon}</div>
        <label class="sc2-toggle">
          <input type="checkbox" class="sc2-rule-cb"${rule.enabled ? ' checked' : ''}>
          <span class="sc2-toggle-track"></span>
        </label>
      </div>
      <div class="sc2-rc-name">${escHtml(displayName)}</div>
      <select class="ic-select sc2-rc-select"></select>
      <div class="sc2-rc-weight">
        <input type="number" class="sc2-weight-input" min="0" max="100" value="${rule.weight}"${rule.enabled ? '' : ' disabled'}>
        <span class="sc2-rc-pct">%</span>
      </div>
      <input type="range" class="sc2-weight-slider" min="0" max="100" value="${rule.weight}"${rule.enabled ? '' : ' disabled'}>
    `.trim();

    // Populate select — always enabled regardless of rule.enabled
    const sel = card.querySelector<HTMLSelectElement>('.sc2-rc-select')!;
    rule.candidates.forEach(c => {
      const opt = document.createElement('option');
      opt.value       = c.id;
      opt.textContent = c.name;
      opt.selected    = c.id === rule.id;
      sel.appendChild(opt);
    });
    sel.addEventListener('change', () => {
      const picked = rule.candidates.find(c => c.id === sel.value)!;
      rule.id   = picked.id;
      rule.name = picked.name;
      saveRuleSelection(rule.key, picked.id);
    });

    const cb     = card.querySelector<HTMLInputElement>('.sc2-rule-cb')!;
    const numIn  = card.querySelector<HTMLInputElement>('.sc2-weight-input')!;
    const slider = card.querySelector<HTMLInputElement>('.sc2-weight-slider')!;

    cb.addEventListener('change', () => {
      rule.enabled = cb.checked;
      card.classList.toggle('active',   cb.checked);
      card.classList.toggle('inactive', !cb.checked);
      numIn.disabled  = !cb.checked;
      slider.disabled = !cb.checked;
      saveWeights(_ruleConfigs);
      updateWeightStatus();
    });

    numIn.addEventListener('input', () => {
      const v = Math.max(0, Math.min(100, parseInt(numIn.value, 10) || 0));
      numIn.value  = String(v);
      slider.value = String(v);
      rule.weight  = v;
      saveWeights(_ruleConfigs);
      updateWeightStatus();
    });

    slider.addEventListener('input', () => {
      const v = parseInt(slider.value, 10);
      numIn.value = String(v);
      rule.weight = v;
      saveWeights(_ruleConfigs);
      updateWeightStatus();
    });

    panel.appendChild(card);
  });

  updateWeightStatus();
}

function updateWeightStatus(): void {
  const statusEl = document.getElementById('safetyWeightStatus');
  const msgEl    = document.getElementById('safetyRuleMsg');
  const chipEl   = document.getElementById('safetyWeightChip');
  const runBtn   = document.getElementById('safetyRunBtn') as HTMLButtonElement | null;

  const enabled = _ruleConfigs.filter(r => r.enabled);
  const total   = enabled.reduce((s, r) => s + r.weight, 0);
  const count   = enabled.length;

  const sumOk   = total === 100;
  const countOk = count >= 4 && count <= 6;
  const valid   = sumOk && countOk;

  if (statusEl) {
    statusEl.textContent = `${total}%`;
    statusEl.className   = 'sc2-weight-total ' + (sumOk ? 'valid' : 'invalid');
  }

  if (chipEl) {
    chipEl.className = 'sc2-weight-chip ' + (sumOk ? 'valid' : 'invalid');
  }

  if (msgEl) {
    if (!countOk)    msgEl.textContent = count < 4 ? `Select ${4 - count} more rule(s)` : 'Max 6 rules';
    else if (!sumOk) msgEl.textContent = `Weights must total 100% (${total > 100 ? '-' : '+'}${Math.abs(100 - total)}%)`;
    else             msgEl.textContent = '';
  }

  if (runBtn) runBtn.disabled = !valid;
}

// ── Run button ─────────────────────────────────────────────────────────────

function wireRunButton(): void {
  document.getElementById('safetyRunBtn')!.addEventListener('click', async () => {
    if (!_data) return;

    const deviceMap = new Map(_data.devices.map(d => [d.id, d.name]));

    // Apply group filter — a selected parent group includes vehicles in its descendant groups.
    const selectedGroups    = getSelectedGroupIds();
    const allGroupsSelected = selectedGroups.size === _data.groups.length;
    const groupFilteredIds  = allGroupsSelected
      ? _data.devices.map(d => d.id)
      : (() => {
          const expanded = expandGroups(selectedGroups, _data.groupChildren);
          return _data.devices.filter(d => d.groupIds.some(gid => expanded.has(gid))).map(d => d.id);
        })();
    const groupFilteredSet = new Set(groupFilteredIds);

    const vehicleIds = _scoreBy === 'driver'
      ? groupFilteredSet
      : new Set(getSelectedVehicleIds());

    await runSafetyAnalysis({
      ruleConfigs: _ruleConfigs.filter(r => r.enabled),
      scoreBy:     _scoreBy,
      vehicleIds,
      driverIds:   new Set(getSelectedDriverIds()),
      distUnit:    _distUnit,
      userMap:     _data.userMap,
      deviceMap,
      ...getDateRange(),
    });
  });

  // Close dropdowns on outside click
  document.addEventListener('click', () => {
    document.getElementById('safetyVselectorPanel')?.classList.add('hidden');
    document.getElementById('safetyDselectorPanel')?.classList.add('hidden');
    document.getElementById('safetyGselectorPanel')?.classList.add('hidden');
  });
}

// ── Export button (lazy-loaded chunk) ─────────────────────────────────────

function wireExportButton(): void {
  document.getElementById('safetyExportBtn')!.addEventListener('click', async () => {
    const { exportSafetyXlsx } = await import('./safety-export');
    exportSafetyXlsx();
  });
}

// ── Data load ──────────────────────────────────────────────────────────────

async function loadData(): Promise<void> {
  if (_dataLoaded) return;
  showState('loading');
  try {
    _data        = await loadSafetyPageData();
    _dataLoaded  = true;
    _ruleConfigs = _data.ruleConfigs.map(r => ({ ...r }));
    renderRuleConfig();
    populateVselector(_data);
    populateDselector(_data);
    populateGroupSelector(_data);
    showState('empty');
  } catch (err) {
    showState('error', 'Failed to load safety data: ' + (err as Error).message);
  }
}

// ── State helpers ──────────────────────────────────────────────────────────

function showState(state: 'empty' | 'loading' | 'error', msg = ''): void {
  document.getElementById('safetyEmpty')  !.classList.toggle('hidden', state !== 'empty');
  document.getElementById('safetyLoading')!.classList.toggle('hidden', state !== 'loading');
  document.getElementById('safetyErrBox') !.classList.toggle('hidden', state !== 'error');
  document.getElementById('safetyResults')!.classList.add('hidden');
  if (state === 'loading') {
    const lbl = document.getElementById('safetyLoadingLabel');
    if (lbl) lbl.textContent = msg || 'Loading safety data…';
  }
  if (state === 'error') {
    document.getElementById('safetyErrBox')!.textContent = msg;
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────

function escHtml(s: string): string {
  return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
}
function escAttr(s: string): string {
  return escHtml(s).replace(/"/g,'&quot;');
}

function getSelectedVehicleIds(): string[] {
  return [...document.querySelectorAll<HTMLInputElement>('#safetyVselectorList input[type="checkbox"]:checked')]
    .map(cb => cb.value);
}

function getSelectedDriverIds(): string[] {
  return [...document.querySelectorAll<HTMLInputElement>('#safetyDselectorList input[type="checkbox"]:checked')]
    .map(cb => cb.value);
}

function getDateRange(): { fromISO: string; toISO: string } {
  const fromVal = (document.getElementById('safetyFrom') as HTMLInputElement).value;
  const toVal   = (document.getElementById('safetyTo')   as HTMLInputElement).value;
  return {
    fromISO: new Date(fromVal + 'T00:00:00').toISOString(),
    toISO:   new Date(toVal   + 'T23:59:59').toISOString(),
  };
}

// ── Exports used by dashboard.ts ───────────────────────────────────────────

export { getSelectedVehicleIds, getSelectedDriverIds, getDateRange };
export function getScoreBy():  ScoreBy  { return _scoreBy;  }
export function getDistUnit(): DistUnit { return _distUnit; }
